import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import officeService from "./officeService";
import { flashMessage as flash } from "./../../../utils/flash/index";

const initialState = {
  offices: [],
  single_office: {},
  isLoading: false,
  isSuccess: false,
  isError: false,
  searchLoading: false,
  searchedOffices: null,
  message: "",
};

// Fetch offices
// Use '_,' as first argument if first argument is empty
export const fetchAllOffices = createAsyncThunk(
  "/office/getAll",
  async (data, thunkAPI) => {
    try {
      // Get token from state using thunkAPI
      // pass token as parameter
      // const token = thunkAPI.getState().auth?.user?.token || null
      return await officeService.fetchAllOffices(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Fetch Single Office
export const fetchSingleOffice = createAsyncThunk(
  "/offices/id",
  async (id, thunkAPI) => {
    try {
      // Get token from state using thunkAPI
      // pass token as parameter
      // const token = thunkAPI.getState().auth?.user?.token || null
      return await officeService.fetchSingleOffice(id);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Add New Office
export const addNewOffice = createAsyncThunk(
  "/office/add",
  async (officeData, thunkAPI) => {
    try {
      return await officeService.addNewOffice(officeData);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update Office
export const updateOffice = createAsyncThunk(
  "/office/update",
  async (officeData, thunkAPI) => {
    try {
      return await officeService.updateOffice(officeData);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// search users
export const searchOffices = createAsyncThunk(
  "office/searchOffices",
  async (data, thunkAPI) => {
    try {
      return await officeService.searchOffices(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const officeSlice = createSlice({
  name: "office",
  initialState,
  reducers: {
    resetOffices: (state) => initialState,
    resetStatus: (state) => {
      state.isError = false;
      state.isSuccess = false;
      state.isLoading = false;
      state.searchLoading = false;
      state.message = "";
    },
    resetSingleOffice: (state) => {
      state.single_office = {};
    },
    resetSearchedOffices: (state) => {
      state.searchedOffices = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetching Offices
      .addCase(fetchAllOffices.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllOffices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.offices = action.payload.data;
      })
      .addCase(fetchAllOffices.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // SearchUsers
      .addCase(searchOffices.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(searchOffices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.searchLoading = false;
        if (action.payload.search_all_offices) {
          state.searchedOffices = action.payload.search_all_offices;
        }
      })
      .addCase(searchOffices.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Fetching Single Office
      .addCase(fetchSingleOffice.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchSingleOffice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.single_office = action.payload.data;
      })
      .addCase(fetchSingleOffice.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        flash("error", "Resource not found");
      })
      // Add New Office
      .addCase(addNewOffice.pending, (state) => {
        state.isLoading = true;
        flash("info", "Creating Office...");
      })
      .addCase(addNewOffice.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.data) {
          state.isLoading = false;
          state.isSuccess = true;
          state.offices.offices.push(action.payload.data);
          flash("success", "Office created successfully");
        } else if (action.payload.errors.office_code) {
          flash("error", action.payload.errors.office_code[0]);
        } else if (action.payload.errors.office_name) {
          flash("error", action.payload.errors.office_name[0]);
        }
      })
      // Update Office
      .addCase(updateOffice.pending, (state) => {
        state.isLoading = true;
        flash("loading", "Updating...");
      })
      .addCase(updateOffice.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
        flash("success", "Updated Successfully");
      })
      .addCase(updateOffice.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        flash("error", action.payload);
      });
  },
});

export const {
  resetOffices,
  resetStatus,
  resetSingleOffice,
  testingState,
  resetSearchedOffices,
} = officeSlice.actions;
export default officeSlice.reducer;
